import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchHeadlessOverview } from '../utils/api';
import '../styles/HeadlessReviewPage.css';

function HeadlessReviewPage() {
  const { user } = useAuth();
  const [sort, setSort] = useState('owner_user');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedOwners, setExpandedOwners] = useState({});
  const [expandedIntegrations, setExpandedIntegrations] = useState({});
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!user || (user.accountType !== 'admin' && user.accountType !== 'superadmin')) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const overview = await fetchHeadlessOverview(sort);
        if (!cancelled) setData(overview);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load overview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, sort]);

  if (!user || (user.accountType !== 'admin' && user.accountType !== 'superadmin')) {
    return (
      <div className="headless-review-page">
        <div className="alert alert-error">You do not have permission to access Headless review.</div>
      </div>
    );
  }

  const toggleOwner = (id) => {
    setExpandedOwners((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleIntegration = (id) => {
    setExpandedIntegrations((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const filterText = filter.trim().toLowerCase();

  const filteredSubUsers = (data?.sub_users || []).filter((row) => {
    if (!filterText) return true;
    return [
      row.external_key,
      row.owner_email,
      row.integration_name,
      row.integration_client_id,
      row.suffix,
    ].some((v) => String(v || '').toLowerCase().includes(filterText));
  });

  return (
    <div className="headless-review-page">
      <div className="headless-page-header">
        <h1>Headless Review</h1>
        <p>Operational oversight of integrations and sub-users. Each auto-project is bound to a horizon; tools and data come from that horizon, not from the admin’s current Active Horizon.</p>
      </div>

      <div className="headless-toolbar">
        <div className="headless-sort-tabs">
          <button
            type="button"
            className={sort === 'owner_user' ? 'active' : ''}
            onClick={() => setSort('owner_user')}
          >
            By owner user
          </button>
          <button
            type="button"
            className={sort === 'sub_user' ? 'active' : ''}
            onClick={() => setSort('sub_user')}
          >
            By sub-user
          </button>
        </div>
        <input
          type="search"
          className="headless-filter"
          placeholder="Filter by user, integration, or external key…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      {loading && <div className="headless-loading">Loading…</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {!loading && !error && sort === 'owner_user' && (
        <div className="headless-owner-groups">
          {(data?.owners || []).map((owner) => {
            const ownerKey = owner.owner_user_id;
            const isOpen = expandedOwners[ownerKey] !== false;
            const integrations = (owner.integrations || []).filter((intg) => {
              if (!filterText) return true;
              return [
                owner.owner_email,
                intg.name,
                intg.client_id,
                intg.default_suffix,
              ].some((v) => String(v || '').toLowerCase().includes(filterText));
            });
            if (!integrations.length && filterText) return null;
            return (
              <div key={ownerKey} className="headless-owner-card">
                <button type="button" className="headless-owner-header" onClick={() => toggleOwner(ownerKey)}>
                  <i className={`fas fa-chevron-${isOpen ? 'down' : 'right'}`} />
                  <span className="headless-owner-email">{owner.owner_email || ownerKey}</span>
                  <span className="headless-badge">{integrations.length} integration(s)</span>
                </button>
                {isOpen && integrations.map((intg) => {
                  const intgOpen = expandedIntegrations[intg.id] !== false;
                  const subs = (intg.sub_users || []).filter((su) => {
                    if (!filterText) return true;
                    return String(su.external_key || '').toLowerCase().includes(filterText)
                      || String(su.suffix || '').toLowerCase().includes(filterText);
                  });
                  return (
                    <div key={intg.id} className="headless-integration-block">
                      <button type="button" className="headless-integration-header" onClick={() => toggleIntegration(intg.id)}>
                        <i className={`fas fa-chevron-${intgOpen ? 'down' : 'right'}`} />
                        <strong>{intg.name}</strong>
                        <code>{intg.client_id}</code>
                        <span className="headless-horizon-chip" title="Horizon for auto-projects, tools, and data">
                          Horizon: <code>{intg.default_suffix || 'owner active'}</code>
                        </span>
                        {intg.revoked_at && <span className="headless-revoked">Revoked</span>}
                        <span className="headless-badge">{subs.length} sub-user(s)</span>
                      </button>
                      {intgOpen && (
                        <table className="headless-table">
                          <thead>
                            <tr>
                              <th>External key</th>
                              <th>Sub-user ID</th>
                              <th>Horizon</th>
                              <th>Project</th>
                              <th>Last token</th>
                              <th>Last activity</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subs.map((su) => (
                              <tr key={su.sub_user_id}>
                                <td><code>{su.external_key}</code></td>
                                <td><code className="headless-mono-sm">{su.sub_user_id}</code></td>
                                <td><code>{su.suffix || intg.default_suffix || '—'}</code></td>
                                <td>
                                  <Link to={`/p/${su.project_id}`}>View project</Link>
                                </td>
                                <td>{su.last_token_at ? new Date(su.last_token_at).toLocaleString() : '—'}</td>
                                <td>{su.last_activity_at ? new Date(su.last_activity_at).toLocaleString() : '—'}</td>
                              </tr>
                            ))}
                            {!subs.length && (
                              <tr><td colSpan={6} className="headless-empty">No sub-users yet</td></tr>
                            )}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
          {!(data?.owners || []).length && <div className="headless-empty">No integrations configured yet.</div>}
        </div>
      )}

      {!loading && !error && sort === 'sub_user' && (
        <table className="headless-table headless-flat-table">
          <thead>
            <tr>
              <th>External key</th>
              <th>Sub-user ID</th>
              <th>Horizon</th>
              <th>Project</th>
              <th>Integration</th>
              <th>Owner</th>
              <th>Created</th>
              <th>Last token</th>
              <th>Last activity</th>
            </tr>
          </thead>
          <tbody>
            {filteredSubUsers.map((row) => (
              <tr key={row.sub_user_id}>
                <td><code>{row.external_key}</code></td>
                <td><code className="headless-mono-sm">{row.sub_user_id}</code></td>
                <td><code>{row.suffix || '—'}</code></td>
                <td><Link to={`/p/${row.project_id}`}>View</Link></td>
                <td>{row.integration_name} <code className="headless-mono-sm">{row.integration_client_id}</code></td>
                <td>{row.owner_email || row.owner_user_id}</td>
                <td>{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</td>
                <td>{row.last_token_at ? new Date(row.last_token_at).toLocaleString() : '—'}</td>
                <td>{row.last_activity_at ? new Date(row.last_activity_at).toLocaleString() : '—'}</td>
              </tr>
            ))}
            {!filteredSubUsers.length && (
              <tr><td colSpan={9} className="headless-empty">No sub-users found</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default HeadlessReviewPage;
